export const WORLD_DOCUMENT_KINDS = [
  "character",
  "npc",
  "item",
  "skill",
  "location",
  "condition",
  "faction",
  "quest",
  "scene",
  "world_clock",
  "rule",
  "epoch_checkpoint",
] as const;

/**
 * Built-in kinds are useful defaults, not a closed genre schema. Creator
 * projects may introduce any stable kind through their File Type registry.
 */
export type BuiltInWorldDocumentKind = (typeof WORLD_DOCUMENT_KINDS)[number];
export type WorldDocumentKind = string;
export type ContextVisibility = "pinned" | "partial" | "searchable" | "hidden";
export type ContextTriggerMode = "default" | "narrative" | "gm_tool";
export type DisplayVisibility = "public" | "player" | "creator" | "gm" | "hidden";
export type EditPermission = "player" | "gm" | "creator" | "system";
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export interface WorldReference {
  relation: string;
  targetId: string;
  metadata?: JsonObject;
}

export interface WorldDocument<TFields extends JsonObject = JsonObject> {
  id: string;
  storyId: string;
  kind: WorldDocumentKind;
  schemaVersion: number;
  revision: number;
  title: string;
  summary: string;
  fields: TFields;
  /** Engine-owned deterministic state. File Type schemas validate `fields`; tools own this envelope. */
  runtimeState?: JsonObject;
  references: WorldReference[];
  visibility: ContextVisibility;
  displayVisibility?: DisplayVisibility;
  editableBy?: EditPermission[];
  fileTypeId?: string;
  aliases?: string[];
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export type MutationOperation = "create" | "replace" | "delete";

export interface WorldMutation {
  operation: MutationOperation;
  documentId: string;
  expectedRevision: number;
  document?: WorldDocument;
  reason?: string;
  sourceMessageIds?: string[];
}

export interface WorldTransaction {
  id: string;
  storyId: string;
  actor: "player" | "creator" | "narrator" | "memory_worker" | "world_worker" | "system";
  createdAt: string;
  mutations: WorldMutation[];
}

export interface WorldEvent {
  transactionId: string;
  eventIndex: number;
  storyId: string;
  actor: WorldTransaction["actor"];
  operation: MutationOperation;
  documentId: string;
  beforeRevision: number;
  afterRevision: number;
  before?: WorldDocument;
  after?: WorldDocument;
  reason?: string;
  sourceMessageIds: string[];
  createdAt: string;
}

export interface TransactionResult {
  documents: WorldDocument[];
  events: WorldEvent[];
}

export interface WorldValueDiff {
  path: string[];
  kind: "added" | "removed" | "changed";
  before?: JsonValue;
  after?: JsonValue;
}

export interface WorldEventDiff {
  transactionId: string;
  eventIndex: number;
  documentId: string;
  operation: MutationOperation;
  actor: WorldEvent["actor"];
  reason?: string;
  sourceMessageIds: string[];
  changes: WorldValueDiff[];
  createdAt: string;
}

export class RevisionConflictError extends Error {
  readonly documentId: string;
  readonly expectedRevision: number;
  readonly actualRevision: number;

  constructor(documentId: string, expectedRevision: number, actualRevision: number) {
    super(`Revision conflict for ${documentId}: expected ${expectedRevision}, found ${actualRevision}`);
    this.name = "RevisionConflictError";
    this.documentId = documentId;
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}
